import { useParams, Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Users, Building2, Factory, Leaf, ShieldCheck, Target, FileCheck, Phone, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";

const servicesData = {
  "iso-9001": {
    id: "iso9001",
    title: "ISO 9001 Quality Management",
    headline: "Build a World-Class Quality Management System",
    description: "ISO 9001 is the internationally recognized standard for Quality Management Systems (QMS). It provides a framework for organizations to consistently meet customer requirements, enhance satisfaction, and drive continuous improvement across all operations.",
    problem: "Many organizations struggle with inconsistent processes, quality defects, customer complaints, and difficulty meeting regulatory requirements. Without a structured approach, businesses waste resources on rework and lose customer trust.",
    whatWeOffer: [
      "Gap Analysis & Readiness Assessment - Evaluate your current practices against ISO 9001 requirements",
      "System Design & Documentation - Develop quality policy, procedures, and work instructions tailored to your operations",
      "Implementation Support - Guide your team through process mapping, risk-based thinking, and performance monitoring",
      "Internal Audit Training & Execution - Build internal audit capability and conduct audits to verify conformance",
      "Pre-Certification Audit - Mock audit to identify gaps before your certification body arrives",
      "Certification Support - Liaison with certification bodies and support during external audits",
      "Ongoing Maintenance & Surveillance Audit Preparation - Ensure sustained compliance year after year"
    ],
    idealFor: [
      "Manufacturing companies seeking operational excellence",
      "Service organizations wanting to standardize processes",
      "Companies entering new markets requiring ISO 9001 certification",
      "Organizations looking to reduce waste and improve efficiency",
      "Businesses pursuing government or corporate contracts that mandate ISO 9001"
    ],
    benefits: [
      "Enhanced customer satisfaction through consistent quality",
      "Reduced operational costs via process optimization",
      "Access to new markets and business opportunities",
      "Improved employee engagement and clarity of roles",
      "Structured approach to risk management"
    ],
    icon: ShieldCheck,
    color: "text-blue-600",
    bgColor: "bg-blue-50"
  },
  "iso-22000": {
    id: "iso22000",
    title: "ISO 22000 / FSSC 22000 Food Safety",
    headline: "Achieve GFSI-Recognized Food Safety Excellence",
    description: "ISO 22000 and FSSC 22000 are globally recognized standards for Food Safety Management Systems (FSMS). FSSC 22000 is benchmarked by the Global Food Safety Initiative (GFSI), making it a requirement for suppliers to major retailers and food manufacturers worldwide.",
    problem: "Food businesses face increasing pressure from customers, regulators, and consumers to demonstrate robust food safety controls. Without a structured FSMS, organizations risk product recalls, regulatory penalties, loss of contracts, and damage to brand reputation.",
    whatWeOffer: [
      "FSMS Gap Analysis - Assess your current food safety practices against ISO 22000/FSSC 22000 requirements",
      "HACCP Plan Development - Create and validate hazard analysis and critical control points specific to your products",
      "Prerequisite Programs (PRPs) - Develop and implement PRPs including sanitation, pest control, allergen management, and more",
      "System Documentation - Quality manual, procedures, SOPs, and record templates customized to your facility",
      "Staff Training - Food safety awareness, HACCP principles, internal auditor training",
      "Internal Audit Program - Build and execute an internal audit schedule to verify system effectiveness",
      "Pre-Certification Audit - Identify and close gaps before your certification audit",
      "Certification Body Liaison - Support during external audits and GFSI scheme requirements"
    ],
    idealFor: [
      "Food manufacturers and processors",
      "Beverage producers",
      "Food packaging companies",
      "Cold storage and distribution facilities",
      "Suppliers to major retailers (Walmart, Costco, Kroger, etc.)",
      "Export-focused food businesses"
    ],
    benefits: [
      "GFSI recognition opens doors to global markets",
      "Reduced risk of food safety incidents and recalls",
      "Enhanced customer and consumer confidence",
      "Improved operational efficiency and traceability",
      "Regulatory compliance across multiple jurisdictions"
    ],
    icon: FileCheck,
    color: "text-green-600",
    bgColor: "bg-green-50"
  },
  "haccp-gmp": {
    id: "haccp",
    title: "HACCP & GMP",
    headline: "Protect Your Consumers with Proven Food Safety Controls",
    description: "Hazard Analysis and Critical Control Points (HACCP) is a systematic approach to identifying, evaluating, and controlling food safety hazards. Combined with Good Manufacturing Practices (GMP), these form the foundation of any food safety program and are often regulatory requirements.",
    problem: "Without proper hazard analysis and prerequisite programs, food businesses operate reactively, addressing problems after they occur. This leads to product contamination, regulatory enforcement actions, and potential harm to consumers.",
    whatWeOffer: [
      "HACCP Plan Development - Conduct hazard analysis, identify CCPs, establish critical limits, monitoring procedures, corrective actions, and verification activities",
      "GMP Assessment & Development - Evaluate and improve your prerequisite programs including sanitation, personal hygiene, facility maintenance, and pest control",
      "HACCP Team Training - Train your team on the 7 HACCP principles and 12 implementation steps",
      "Validation Studies - Verify that your critical limits effectively control identified hazards",
      "HACCP Plan Review & Updates - Annual review and updates to reflect process or product changes",
      "Regulatory Compliance Support - Align your HACCP program with FDA, USDA, CFIA, and other regulatory requirements"
    ],
    idealFor: [
      "Food processing facilities",
      "Restaurants and catering operations",
      "Food service establishments",
      "Small and medium-sized food manufacturers",
      "Businesses preparing for more advanced certifications (SQF, FSSC 22000)",
      "Companies subject to regulatory inspection"
    ],
    benefits: [
      "Proactive hazard prevention vs. reactive crisis management",
      "Demonstrated due diligence for regulatory compliance",
      "Foundation for GFSI certification pathways",
      "Reduced product waste and recalls",
      "Enhanced staff food safety awareness"
    ],
    icon: ShieldCheck,
    color: "text-orange-600",
    bgColor: "bg-orange-50"
  },
  "gap-globalgap": {
    id: "gap",
    title: "GAP / GlobalGAP",
    headline: "Demonstrate Responsible Agricultural Practices",
    description: "Good Agricultural Practices (GAP) and GlobalGAP certification demonstrate that your agricultural products are produced safely and sustainably. GlobalGAP is the world's leading farm assurance program, recognized by major retailers globally.",
    problem: "Agricultural producers often struggle to access premium markets due to lack of certification. Without documented practices, farms face challenges demonstrating food safety, environmental responsibility, and worker welfare to buyers and auditors.",
    whatWeOffer: [
      "Gap Assessment - Evaluate current farm practices against GAP/GlobalGAP requirements",
      "Documentation Development - Farm management records, pest management plans, chemical usage records, traceability systems",
      "Food Safety on Farm - Implement pre-harvest and post-harvest food safety controls",
      "Environmental Management - Water management, waste handling, biodiversity conservation practices",
      "Worker Health, Safety & Welfare - Training programs, PPE protocols, hygiene facilities",
      "Internal Inspection Training - Train staff to conduct internal farm inspections",
      "Pre-Certification Support - Mock audits and corrective action guidance",
      "Certification Body Coordination - Support during GlobalGAP audits"
    ],
    idealFor: [
      "Fruit and vegetable farms",
      "Aquaculture operations",
      "Livestock producers",
      "Agricultural cooperatives",
      "Export-focused farms targeting EU, USA, and UK markets",
      "Suppliers to major supermarket chains"
    ],
    benefits: [
      "Access to premium international markets",
      "Higher prices for certified products",
      "Improved farm management and record-keeping",
      "Demonstrated commitment to sustainability",
      "Enhanced buyer and consumer confidence"
    ],
    icon: Leaf,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50"
  },
  "sqf": {
    id: "sqf",
    title: "Safe Quality Food (SQF)",
    headline: "Achieve GFSI-Recognized Food Safety and Quality Certification",
    description: "The Safe Quality Food (SQF) Program is a rigorous, GFSI-benchmarked certification covering food safety and quality management. SQF is widely recognized by retailers, foodservice providers, and manufacturers worldwide, particularly in North America.",
    problem: "Food businesses supplying to major North American retailers often require SQF certification. Without it, companies lose access to significant sales channels. The SQF audit is rigorous, and unprepared organizations face costly failed audits and delayed certifications.",
    whatWeOffer: [
      "SQF Gap Analysis - Assess readiness against SQF Code requirements (Edition 9)",
      "Food Safety Plan Development - HACCP-based food safety plans meeting SQF requirements",
      "Quality Plan Development - Product specifications, supplier approval, customer complaint handling",
      "SQF System Documentation - Site master document, procedures, work instructions, and records",
      "SQF Practitioner Training - Prepare your SQF Practitioner for their role in the system",
      "Internal Audit Program - Develop and execute internal audits aligned with SQF requirements",
      "Pre-Certification Audit - Identify and close critical gaps before your certification audit",
      "Certification Support - Guidance through the SQF audit process and corrective action follow-up"
    ],
    idealFor: [
      "Food manufacturers supplying to North American retailers",
      "Food processors requiring GFSI certification",
      "Co-packers and private label producers",
      "Companies transitioning from HACCP to GFSI certification",
      "Businesses requiring both food safety and quality certification"
    ],
    benefits: [
      "Access to North American retail giants",
      "Combined food safety and quality management",
      "Rigorous program demonstrating commitment to excellence",
      "Clear roadmap through SQF Fundamentals, Level 2, and Level 3",
      "Competitive advantage in the marketplace"
    ],
    icon: ShieldCheck,
    color: "text-purple-600",
    bgColor: "bg-purple-50"
  },
  "auditing": {
    id: "audit",
    title: "Internal & Supplier Auditing Services",
    headline: "Verify Compliance and Drive Continuous Improvement",
    description: "Auditing is essential to verify that management systems are implemented effectively and identify opportunities for improvement. Our experienced auditors conduct thorough, value-added audits that go beyond compliance checking to deliver actionable insights.",
    problem: "Organizations often lack the expertise or objectivity to conduct effective audits. Internal teams may miss critical issues, while inadequate supplier audits expose businesses to supply chain risks including quality failures, food safety incidents, and regulatory non-compliance.",
    whatWeOffer: [
      "Internal Audits - Comprehensive audits of your management system against ISO 9001, ISO 22000, FSSC 22000, SQF, or other standards",
      "Supplier Audits - On-site evaluation of suppliers against your specifications and industry standards",
      "Second-Party Audits - Audits conducted on behalf of customers or partners",
      "Pre-Certification Audits - Mock audits simulating certification body requirements",
      "Gap Assessments - Baseline evaluations for organizations starting their certification journey",
      "Audit Training - Internal auditor courses to build your team's audit capability",
      "Audit Program Development - Design and implement an effective audit schedule and methodology"
    ],
    idealFor: [
      "Organizations maintaining certified management systems",
      "Companies with complex supply chains requiring supplier oversight",
      "Businesses preparing for certification or surveillance audits",
      "Organizations seeking objective, expert assessment of their systems",
      "Companies wanting to build internal audit capability"
    ],
    benefits: [
      "Objective identification of system gaps and risks",
      "Improved supplier performance and reduced supply chain risk",
      "Better preparation for certification audits",
      "Actionable findings that drive real improvement",
      "Knowledge transfer to your internal team"
    ],
    icon: Target,
    color: "text-red-600",
    bgColor: "bg-red-50"
  },
  "iso-31000": {
    id: "iso31000",
    title: "ISO 31000 Risk Management",
    headline: "Build Organizational Resilience Through Effective Risk Management",
    description: "ISO 31000 provides guidelines for managing risk across an organization. It helps businesses identify, assess, and treat risks systematically, enabling informed decision-making and improved outcomes across all activities.",
    problem: "Organizations face increasingly complex risks from operational disruptions, regulatory changes, cyber threats, supply chain failures, and reputational damage. Without a structured approach, businesses make reactive decisions and fail to capitalize on opportunities while exposing themselves to preventable losses.",
    whatWeOffer: [
      "Risk Management Framework Design - Develop a risk management framework aligned with ISO 31000 principles",
      "Risk Assessment Facilitation - Conduct risk identification, analysis, and evaluation workshops",
      "Risk Register Development - Create and maintain a comprehensive organizational risk register",
      "Risk Treatment Planning - Develop risk mitigation strategies and action plans",
      "Risk Appetite & Tolerance Definition - Establish clear parameters for risk acceptance",
      "Business Continuity Integration - Align risk management with business continuity planning",
      "Training & Awareness - Risk management training for leadership and staff",
      "Ongoing Support - Periodic risk reviews and register updates"
    ],
    idealFor: [
      "Organizations seeking structured enterprise risk management",
      "Businesses facing complex regulatory environments",
      "Companies wanting to improve strategic decision-making",
      "Organizations integrating risk management with existing ISO systems",
      "Boards and leadership teams seeking risk governance frameworks"
    ],
    benefits: [
      "Proactive identification and mitigation of threats",
      "Improved strategic and operational decision-making",
      "Enhanced stakeholder confidence",
      "Reduced losses from preventable events",
      "Better allocation of resources to manage uncertainty"
    ],
    icon: ShieldCheck,
    color: "text-indigo-600",
    bgColor: "bg-indigo-50"
  },
  "market-access": {
    id: "regulatory",
    title: "Market Access & Export Compliance",
    headline: "Navigate Complex Export Requirements with Confidence",
    description: "Exporting food and agricultural products requires compliance with destination country regulations, which vary significantly between markets. Our market access services help you understand and meet the requirements for USA, Canada, EU, UK, and other key markets.",
    problem: "Export-focused businesses often lose shipments, face costly delays, or miss market opportunities due to unfamiliarity with foreign regulatory requirements. Each market has unique registration, labeling, testing, and documentation requirements that are difficult to navigate without expertise.",
    whatWeOffer: [
      "Market Requirements Analysis - Research and document specific regulatory requirements for your target markets",
      "FDA Compliance (USA) - FSMA, FSVP, facility registration, prior notice, labeling compliance",
      "CFIA Compliance (Canada) - SFCR requirements, licensing, traceability",
      "EU Compliance - Official controls, health certificates, labeling regulations, organic certification",
      "UK Compliance - Post-Brexit requirements, IPAFFS, health certificates",
      "Export Documentation - Prepare certificates, attestations, and export declarations",
      "Regulatory Liaison - Coordinate with competent authorities and assist with registrations",
      "Export Readiness Assessment - Evaluate your facility's readiness to meet export market requirements"
    ],
    idealFor: [
      "Food manufacturers targeting international markets",
      "Agricultural exporters (fresh produce, seafood, processed foods)",
      "Businesses currently exporting seeking new markets",
      "Companies receiving inquiries from international buyers",
      "Organizations seeking to diversify market access"
    ],
    benefits: [
      "Reduced risk of shipment rejections and delays",
      "Access to premium international markets",
      "Confidence in regulatory compliance",
      "Competitive advantage through export capability",
      "Clear pathway to market entry"
    ],
    icon: Building2,
    color: "text-cyan-600",
    bgColor: "bg-cyan-50"
  }
};

const ServiceDetail = () => {
  const { serviceSlug } = useParams();
  const service = servicesData[serviceSlug];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Service Not Found</h1>
          <Link to="/services/consulting">
            <Button>View All Services</Button>
          </Link>
        </div>
      </div>
    );
  }

  const ServiceIcon = service.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${service.title} | Compliance Management Systems`}
        description={service.description}
        url={`/services/${serviceSlug}`}
        keywords={[service.title, "consulting", "implementation", "certification", "Jamaica", "Caribbean"]}
      />

      {/* Hero Section */}
      <section className={`${service.bgColor} py-16 lg:py-24`}>
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <Link 
            to="/services/consulting" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to All Services
          </Link>
          
          <div className="flex items-start gap-6">
            <div className={`p-4 rounded-2xl ${service.bgColor} border-2 border-white shadow-sm hidden sm:block`}>
              <ServiceIcon className={`h-12 w-12 ${service.color}`} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl lg:text-4xl text-primary mb-4">
                {service.headline}
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl">
                {service.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content Column */}
            <div className="lg:col-span-2 space-y-10">
              {/* The Problem */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-primary mb-4">
                  The Challenge
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {service.problem}
                </p>
              </div>

              {/* What We Offer */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-primary mb-6">
                  What We Offer
                </h2>
                <div className="space-y-4">
                  {service.whatWeOffer.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className={`h-5 w-5 ${service.color} flex-shrink-0 mt-0.5`} />
                      <span className="text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Benefits */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-primary mb-6">
                  Key Benefits
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.benefits.map((benefit, index) => (
                    <Card key={index} className="border-l-4" style={{ borderLeftColor: service.color.replace('text-', '#').replace('-600', '') }}>
                      <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground">{benefit}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Ideal For Card */}
              <Card className="sticky top-24">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                    <Users className={`h-5 w-5 ${service.color}`} />
                    Ideal For
                  </h3>
                  <ul className="space-y-3">
                    {service.idealFor.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <ArrowRight className="h-4 w-4 text-brand flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  
                  <hr className="my-6" />
                  
                  <div className="space-y-4">
                    <Link to="/services/consulting" className="block">
                      <Button className="w-full bg-brand hover:bg-brand-dark" data-testid="request-consultation-btn">
                        <Phone className="h-4 w-4 mr-2" />
                        Request a Consultation
                      </Button>
                    </Link>
                    <Link to="/contact" className="block">
                      <Button variant="outline" className="w-full">
                        Contact Us
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy-darker text-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl text-center">
          <h2 className="font-heading font-bold text-2xl lg:text-3xl mb-4">
            Ready to Get Started with {service.title}?
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Our experienced consultants are ready to help you achieve your compliance goals. 
            Contact us today for a free initial consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services/consulting">
              <Button size="lg" className="bg-brand hover:bg-brand-dark text-white">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/courses">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-navy-darker">
                View Training Courses
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
